/**
 * DemoPill — low-emphasis demo-tenant indicator in the header chrome.
 *
 * Demo Fidelity sprint design rule: the synthetic-data signal exists in
 * exactly one place — a small gray pill labeled "Demo" in the header
 * chrome, visible only when the active tenant is the demo tenant.
 *
 * Nowhere else. No per-card badges, no per-page warnings. The pill is
 * the single canonical marker that distinguishes the demo tenant from
 * a production district. Every other surface renders identically to
 * production — only the underlying data is synthetic.
 *
 * Tenant identity is resolved via `/api/tenant/context` (same endpoint
 * the older DemoBanner used). On API error the pill fails closed and
 * renders nothing — a production tenant must never see a demo marker
 * because of a transient hiccup.
 */

import React, { useEffect, useState } from 'react';
import { apiClient } from '../../api/client';

interface DemoPillProps {
    /** Test-only override; in production, visibility is driven by /api/tenant/context. */
    forceShow?: boolean;
}

interface TenantContextResponse {
    tenantId: string | null;
    isDemoTenant?: boolean;
}

export function DemoPill({ forceShow = false }: DemoPillProps): React.ReactElement | null {
    const [isDemoTenant, setIsDemoTenant] = useState<boolean>(forceShow);

    useEffect(() => {
        if (forceShow) {
            setIsDemoTenant(true);
            return;
        }
        let cancelled = false;
        apiClient
            .request<TenantContextResponse>('/api/tenant/context')
            .then((data) => {
                if (!cancelled) setIsDemoTenant(!!data?.isDemoTenant);
            })
            .catch(() => {
                if (!cancelled) setIsDemoTenant(false);
            });
        return () => {
            cancelled = true;
        };
    }, [forceShow]);

    if (!isDemoTenant) return null;

    return (
        <span
            data-testid="demo-pill"
            title="Demo tenant — synthetic data, every code path real."
            className="inline-flex items-center px-2 py-0.5 rounded-full
                       text-[11px] font-medium uppercase tracking-wide
                       bg-slate-200 text-slate-600 border border-slate-300
                       select-none"
        >
            Demo
        </span>
    );
}

export default DemoPill;
